import { HTMLAttributes, forwardRef, ReactNode } from 'react';

/**
 * The design's card (OO1): white, a 1px #E5E5E5 hairline, 6px corners, NO shadow at rest.
 * `rounded-xl` is 6px under the OO1 theme tokens (index.css). `elevated` is for overlays only.
 */
interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'elevated' | 'outlined';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  hover?: boolean;
}

const variantStyles = {
  default: 'bg-white border border-gray-200',
  elevated: 'bg-white shadow-md border border-gray-200',
  outlined: 'bg-white border border-gray-300',
};

const paddingStyles = {
  none: '',
  sm: 'p-4',
  md: 'p-5',
  lg: 'p-6',
};

export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = 'default',
      padding = 'none',
      hover = false,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={`
          rounded-xl overflow-hidden
          ${variantStyles[variant]}
          ${paddingStyles[padding]}
          ${hover ? 'transition-colors duration-150 hover:border-gray-400 cursor-pointer' : ''}
          ${className}
        `}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

// Card Header: 16px 20px with an #EFEFEF rule, the design's card head.
interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
}

export const CardHeader = ({
  className = '',
  children,
  ...props
}: CardHeaderProps) => {
  return (
    <div
      className={`px-5 py-4 border-b border-gray-150 ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

// Card Title: 15px semibold Apex black - the design's h2 inside a card.
interface CardTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  children?: ReactNode;
}

export const CardTitle = ({
  className = '',
  children,
  ...props
}: CardTitleProps) => {
  return (
    <h3 className={`text-[15px] leading-tight font-semibold text-gray-900 ${className}`} {...props}>
      {children}
    </h3>
  );
};

// Card Description
interface CardDescriptionProps extends HTMLAttributes<HTMLParagraphElement> {
  children?: ReactNode;
}

export const CardDescription = ({
  className = '',
  children,
  ...props
}: CardDescriptionProps) => {
  return (
    <p className={`text-[11.5px] text-gray-400 mt-1 ${className}`} {...props}>
      {children}
    </p>
  );
};

// Card Content
interface CardContentProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
}

export const CardContent = ({
  className = '',
  children,
  ...props
}: CardContentProps) => {
  return (
    <div className={`px-5 py-5 ${className}`} {...props}>
      {children}
    </div>
  );
};

// Card Footer
interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
}

export const CardFooter = ({
  className = '',
  children,
  ...props
}: CardFooterProps) => {
  return (
    <div
      className={`px-5 py-4 bg-gray-50 border-t border-gray-150 ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};
